import { Checkbox, Dialog, Popover, Tooltip } from "@mui/material";
import {
  ArrowLeft,
  Edit,
  Filter,
  PlusCircle,
  Trash,
  Trash2,
} from "feather-icons-react/build/IconComponents";
import { t } from "i18next";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import API from "../../API/newIndex";
import BulkEditPortfolioCategory from "./BulkEditPortfolioCategory";
import { DataLoad, PlanPermission, TableColumnsContext } from "../../App";
import Breadcrumbs from "../../controllers/breadCrumbs";
import { getInitialPage, replacePageInUrl } from "../../controllers/PageHandleFromQuery";
import { parentNavigate } from "../../controllers/ParentNavigate";
import { useUser } from "../../controllers/UserContext";
import RenderCustomFieldValue from "../../utilities/RenderCustomFieldValue";
import { ButtonComponent, CommonTable, ConfirmationPopup, PageLimitDropDownComponent, PaginationComponent, SearchComponent } from '@shopeasy-dashboard-ui/ui-library';
import { isAscendingOrder } from "../FormInput/CommonTable";
import NoRecords from "../Main/NoRecords";
import Notification from "../Notification";
import * as defaultCustomFields from '../../utilities/defaultCustomTableField';
import useTableApi from "../../utilities/useTableApi";

function PortfolioCategory() {
  const handleTableApi = useTableApi();
  const { tableColumnsData } = useContext(TableColumnsContext);
  const [PortfolioCategory, setAllPortfolioCategory] = useState();
  const { dataLoading, setDataLoading } = useContext(DataLoad);
  const [Page, setPage] = useState(getInitialPage());
  const { setPlanPermission } = useContext(PlanPermission);
  const userContext = useUser();

  const [clearRow, setClearRow] = useState(false);
  const initialLimit = localStorage.getItem("limit");
  const [limit, setLimit] = useState(initialLimit);
  const [search, setSearch] = useState("");
  const { sortData, isUserLoaded } = useUser();
  const savedSort = sortData?.portfoliocategory;

  const [sortBy, setSortBy] = useState(() => savedSort?.sort_by || "");
  const [orderBy, setOrderBy] = useState(() => {
    const isAsc = isAscendingOrder(savedSort?.order_by);
    return {
      orderByValue: savedSort?.order_by ? (isAsc ? "ASC" : "DESC") : "DESC",
      isDecending: savedSort?.order_by ? !isAsc : true,
    };
  });
  const isInitialSortLoaded = useRef(Boolean(savedSort?.sort_by));
  const [openBulkEditModal, setOpenBulkEditModal] = useState(false);

  const apiFieldNames = useMemo(() => {
    const dataObj = tableColumnsData?.data?.data || tableColumnsData?.data || tableColumnsData;
    if (Array.isArray(dataObj?.portfoliocategory)) return dataObj.portfoliocategory;
    if (Array.isArray(dataObj?.portfoliocategories)) return dataObj.portfoliocategories;
    if (Array.isArray(dataObj?.portfolio_category)) return dataObj.portfolio_category;
    if (Array.isArray(dataObj?.portfolio_categories)) return dataObj.portfolio_categories;
    if (Array.isArray(tableColumnsData?.portfoliocategory)) return tableColumnsData.portfoliocategory;
    return [];
  }, [tableColumnsData]);

  const dynamicColumns = useMemo(() => {
    const fixedKeys = new Set([
      '_id', '__v', 'client', 'tenant', 'store_client', 'store_tenant', 'user',
      'lowercase_category', 'parent_category', 'categoryName', 'portfoliocategory_name',
      'description', 'portfolio_count', 'action', 'actions',
      'checkbox', 'selection', 'select', 'drag_handle', 'draghandle', 'drag',
    ]);

    return apiFieldNames
      .filter((fieldName) => typeof fieldName === 'string' && fieldName.trim() && !fixedKeys.has(fieldName))
      .map((fieldName) => {
        const isDotted = fieldName.includes('.');
        const parts = isDotted ? fieldName.split('.') : null;

        const label = fieldName
          .replace(/\./g, ' • ')
          .replace(/[-_]/g, ' ')
          .replace(/([a-z])([A-Z])/g, '$1 $2')
          .replace(/\b\w/g, (c) => c.toUpperCase());

        const getValue = (row) => {
          if (!row) return undefined;
          if (isDotted && parts) {
            return parts.reduce((obj, k) => (obj != null ? obj[k] : undefined), row);
          }
          return row[fieldName];
        };

        const isImageCol =
          fieldName.toLowerCase().includes('image') ||
          fieldName.toLowerCase().includes('photo') ||
          fieldName.toLowerCase().includes('img') ||
          fieldName.toLowerCase().includes('thumbnail') ||
          fieldName.toLowerCase().includes('logo') ||
          fieldName.toLowerCase().includes('banner') ||
          fieldName.toLowerCase().includes('icon');

        return {
          key: fieldName,
          title: label,
          width: isImageCol ? '80px' : '160px',
          minWidth: isImageCol ? '70px' : '110px',
          maxWidth: isImageCol ? '95px' : undefined,
          responsiveWidth: isImageCol ? '70px' : '100px',
          align: isImageCol ? 'text-center' : undefined,
          headerClassName: isImageCol
            ? 'thead-image px-2 text-center whitespace-nowrap'
            : 'px-3 whitespace-nowrap truncate',
          cellClassName: isImageCol
            ? 'table-image px-2 text-center'
            : 'px-3 truncate',
          render: (row) => {
            const val = getValue(row);
            return <RenderCustomFieldValue val={val} fieldName={fieldName} />;
          },
        };
      });
  }, [apiFieldNames]);

  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    replacePageInUrl(Page);
  }, [Page]);

  useEffect(() => {
    if (!isInitialSortLoaded.current && isUserLoaded) {
      isInitialSortLoaded.current = true;
      if (savedSort && savedSort.sort_by) {
        const isAsc = isAscendingOrder(savedSort.order_by);
        const targetOrderBy = isAsc ? "ASC" : "DESC";
        if (savedSort.sort_by !== sortBy || targetOrderBy !== orderBy.orderByValue) {
          setSortBy(savedSort.sort_by);
          setOrderBy({
            orderByValue: targetOrderBy,
            isDecending: !isAsc,
          });
        }
      }
    }
  }, [savedSort, isUserLoaded, sortBy, orderBy.orderByValue]);

  const SortData = (sort_by, explicitOrder) => {
    setSortBy(sort_by);
    let nextOrder = "DESC";
    if (explicitOrder !== undefined) {
      nextOrder = isAscendingOrder(explicitOrder) ? "ASC" : "DESC";
    } else if (sortBy === sort_by) {
      const isCurrentlyAsc = isAscendingOrder(orderBy);
      nextOrder = isCurrentlyAsc ? "DESC" : "ASC";
    } else {
      nextOrder = "DESC";
    }
    setOrderBy({
      orderByValue: nextOrder,
      isDecending: nextOrder === "DESC",
    });
  };

  const [isNotification, setIsNotification] = useState(false);
  const [notificationMsg, setNotificationMsg] = useState("");
  const [severity, setSeverity] = useState();

  const debounceTimeout = useRef(null);

  useEffect(() => {
    if (!isUserLoaded && !isInitialSortLoaded.current) {
      return;
    }

    const trimmed = search?.trim() || "";
    clearTimeout(debounceTimeout.current);
    if (trimmed === "") {
      GetPortfolioCategoriesWithAllParent();
      setClearRow((prev) => !prev);
    } else if (trimmed.length >= 3) {
      debounceTimeout.current = setTimeout(() => {
        GetPortfolioCategoriesWithAllParent();
        setClearRow((prev) => !prev);
      }, 500);
    }
    return () => clearTimeout(debounceTimeout.current);
  }, [Page, limit, sortBy, orderBy, search, isUserLoaded]);

  const GetPortfolioCategoriesWithAllParent = async () => {
    setDataLoading(true);
    try {
      const GetCategories = await API({
        url: `/admin/portfoliocategory/GetPortfolioCategoriesWithParent?page=${Page}&limit=${limit}&searchkey=${search?.length > 2 ? search : ""
          }&sort_by=${sortBy}&order_by=${orderBy.orderByValue}`,
        method: "get",
      });
      if (GetCategories.status === 200 || GetCategories.status === 304) {
        const result = GetCategories?.data;
        setAllPortfolioCategory(result);
      }
    } catch (error) {
      if (error?.response?.status === 882) {
        setPlanPermission({ isPlanPermissions: false, forModule: "portfolio" });
      }
      setIsNotification(true);
      setNotificationMsg(error);
      setSeverity("error");
    } finally {
      setDataLoading(false);
    }
  };

  const [selectedRowsId, setSelectedRowsId] = useState([]);
  const [confirmationPopup, setConfirmationPopup] = useState(false);

  const HandleDelete = async () => {
    try {
      const payload = {
        id: selectedRowsId,
      };
      const data = await API({
        url: `/admin/portfoliocategory/deletePortfolioCategory`,
        method: "post",
        data: payload,
      });
      if (data.status === 200 || data.status === 304) {
        setIsNotification(true);
        setNotificationMsg("Category Removed Successfully");
        setSeverity("success");
        setSelectedRowsId([]);
        setClearRow(!clearRow);
        GetPortfolioCategoriesWithAllParent();
      }
    } catch (error) {
      setIsNotification(true);
      setNotificationMsg(error);
      setSeverity("error");
    }
    setConfirmationPopup(false);
  };

  const handleCloseConfirm = (value) => {
    setConfirmationPopup(value);
  };

  useEffect(() => {
    localStorage.setItem("limit", limit);
  }, [limit]);

  const handleClose = () => {
    setIsNotification(false);
    setNotificationMsg("");
    setSeverity("");
  };

  const [filter, setFilter] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  const handleSelectRow = (e, id) => {
    if (e?.nativeEvent?.shiftKey) {
      const prevIndex = PortfolioCategory?.data?.records?.findIndex(
        (x) => x._id === selectedRowsId[selectedRowsId?.length - 1],
      );
      const currIndex = PortfolioCategory?.data?.records?.findIndex((x) => x._id === id);
      const startIndex = Math.min(prevIndex, currIndex);
      const endIndex = Math.max(prevIndex, currIndex);
      const final = PortfolioCategory?.data?.records?.slice(startIndex, endIndex + 1);

      if (selectedRowsId?.includes(id)) {
        setSelectedRowsId((prev) => [
          ...new Set(prev.filter((x) => !final.find((item) => item._id === x))),
        ]);
      } else {
        setSelectedRowsId((prev) => [...new Set([...prev, ...final.map((item) => item._id)])]);
      }
    } else if (id === "all") {
      if (selectedRowsId?.length > 0) {
        setSelectedRowsId([]);
      } else {
        const selectable = PortfolioCategory?.data?.records?.filter((x) => !x?.default);
        setSelectedRowsId(selectable?.map((x) => x?._id) || []);
      }
    } else {
      const existId = selectedRowsId?.includes(id);
      if (!existId) {
        setSelectedRowsId((prev) => [...prev, id]);
      } else {
        setSelectedRowsId((prev) => prev.filter((x) => x !== id));
      }
    }
  };

  const resetFilter = (e) => {
    e.preventDefault();
    setFilter(false);
    setAnchorEl(null);
    setSearch("");
    setPage(1);
    setLimit(limit === "all" ? 10 : limit);
  };
  const startRec = PortfolioCategory?.data?.currentPage
    ? (PortfolioCategory.data.currentPage - 1) * Number(limit || 10) + 1
    : 0;
  const endRec = PortfolioCategory?.data?.currentPage
    ? Math.min(
      PortfolioCategory.data.currentPage * Number(limit || 10),
      PortfolioCategory.data.totalRecords,
    )
    : 0;

  const columns = [
    {
      key: "checkbox",
      title: (
        <Checkbox
          checked={
            PortfolioCategory?.data?.records?.length > 0 &&
            selectedRowsId?.length === PortfolioCategory?.data?.records?.length
          }
          indeterminate={
            selectedRowsId?.length > 0 &&
            selectedRowsId?.length < PortfolioCategory?.data?.records?.length
          }
          onChange={(e) => handleSelectRow(e, "all")}
        />
      ),
      align: "text-center",
      ignoreRowClick: true,
      width: "50px",
      minWidth: "50px",
      maxWidth: "50px",
      headerClassName: "!px-0 w-[50px] min-w-[50px] max-w-[50px] text-center",
      cellClassName: "!px-0 w-[50px] min-w-[50px] max-w-[50px] text-center",
      render: (row) => (
        <Checkbox
          disabled={row?.default}
          checked={selectedRowsId?.length > 0 && selectedRowsId?.includes(row._id)}
          onChange={(e) => handleSelectRow(e, row._id)}
        />
      ),
    },
    {
      key: "lowercase_category",
      title: t("lang_Portfolio_Category") || "Portfolio Category",
      width: "350px",
      minWidth: "200px",
      headerClassName: "px-3",
      cellClassName: "px-3",
      render: (row) => (
        <span className="capitalize text-xs font-medium text-[var(--primary-text-color)]">
          {row.parent_category || row.portfoliocategory_name || "-"}
        </span>
      ),
    },
    {
      key: "description",
      title: t("lang_description") || "Description",
      width: "400px",
      minWidth: "200px",
      headerClassName: "px-3",
      cellClassName: "px-3",
      render: (row) => (
        <span className="capitalize line-clamp-2 text-xs text-[var(--primary-text-color)]">
          {row?.description ? row.description.replace(/<[^>]*>/g, " ") : "-"}
        </span>
      ),
    },
    {
      key: "portfolio_count",
      title: t("lang_Portfolio_Count") || "Portfolio Count",
      width: "140px",
      maxWidth: "150px",
      headerClassName: "px-3 whitespace-nowrap",
      cellClassName: "px-3 whitespace-nowrap",
      render: (row) => (
        <span className="text-xs font-semibold text-[var(--primary-text-color)]">
          {row?.portfolio_count ?? 0}
        </span>
      ),
    },
    ...dynamicColumns,
    {
      key: "action",
      title: t("lang_action"),
      width: "100px",
      maxWidth: "110px",
      responsiveWidth: "110px",
      headerClassName: "px-3 text-right",
      cellClassName: "px-3 text-right",
      align: "text-right",
      ignoreRowClick: true,
      render: (row) => (
        <div className="flex items-center justify-end w-full space-x-3">
          <Tooltip title="Edit" arrow>
            <button onClick={() => parentNavigate(`/portfolio/categories/${row._id}`)}>
              <Edit className="w-[16px] h-[16px] text-[var(--table-text-color)] hover:text-[var(--primary-text-color)] transition-colors" />
            </button>
          </Tooltip>
          {row?.default !== true && (
            <Tooltip title="Delete" arrow>
              <button
                onClick={(e) => {
                  e.preventDefault();
                  setConfirmationPopup(true);
                  setSelectedRowsId([row?._id]);
                }}
              >
                <Trash className="w-[16px] h-[16px] text-red-500 hover:text-red-600 transition-colors" />
              </button>
            </Tooltip>
          )}
        </div>
      ),
    },
  ];

  const tableHeaderFilter = (manageFieldsBtn) => {
    const totalCount = PortfolioCategory?.data?.totalRecords || PortfolioCategory?.data?.records?.length || 0;
    return (dataLoading || totalCount > 0 || search) ? (
      <div className="relative w-full border-b main-border-color secondary-bg-color px-3 py-2 flex flex-col pe-3 min-h-[50px]">
        <div className="flex flex-wrap items-center justify-between w-full gap-2 min-h-[38px]">
          <div className="hidden md:flex items-center gap-2 ml-auto">
            <div className="w-[180px] md:w-[200px]">
              <SearchComponent
                placeholder="Search Category"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                onClear={() => {
                  setSearch("");
                  setPage(1);
                }}
                isClearable={true}
                size="sm"
                iconPlacement="left"
              />
            </div>

            <div className="w-[80px] md:w-[100px] shrink-0 select-sm-height select-hide-label select-no-padding-y">
              <PageLimitDropDownComponent
                limit={limit}
                setLimit={setLimit}
                setPage={setPage}
                totalRecords={totalCount}
              />
            </div>
            {manageFieldsBtn}
          </div>

          <Tooltip title={t("lang_filter") || "Filter"} arrow>
            <button
              type="button"
              onClick={(e) => {
                setFilter(true);
                setAnchorEl(e.currentTarget);
              }}
              className="block md:hidden relative flex items-center justify-center p-2 border main-border-color rounded-lg bg-[var(--secondary-bg-color)] hover:bg-gray-50/50 transition-colors w-8 h-8 shrink-0"
            >
              <Filter className="h-4 w-4 text-[var(--table-text-color)]" />
            </button>
          </Tooltip>
        </div>

        {selectedRowsId?.length > 0 && (
          <div className="flex flex-wrap items-center gap-1 mt-1 w-full">
            <span className="text-sm px-2 whitespace-nowrap">
              {selectedRowsId?.length} {t("lang_selected")}
            </span>
            <span className="me-1">|</span>
            <ButtonComponent
              variant="outline"
              size="sm"
              className="mx-1"
              onClick={() => setOpenBulkEditModal(true)}
              icon={<Edit className="w-3.5 h-3.5" />}
            >
              {t("lang_bulk_edit", "Bulk Edit")}
            </ButtonComponent>
            <ButtonComponent
              variant="danger_outline"
              size="sm"
              icon={<Trash2 className="w-3.5 h-3.5" />}
              onClick={(e) => {
                e.preventDefault();
                setConfirmationPopup(true);
              }}
            >
              {t("lang_delete_selection")}
            </ButtonComponent>
          </div>
        )}
      </div>
    ) : null;
  };

  return (
    <>
      <ConfirmationPopup
        heading="Delete Portfolio Category"
        subheading="Do you really want to delete this portfolio category from your store?"
        confirmationPopup={confirmationPopup}
        handleCloseConfirm={handleCloseConfirm}
        runFunction={HandleDelete}
      />
      {isNotification && notificationMsg && (
        <Notification message={notificationMsg} close={handleClose} severity={severity} />
      )}
      <div className="h-auto overflow-y-auto overflow-x-hidden pb-[64px] p-5">
        <div className="flex items-end justify-between page-header-pb">
          <div className="flex items-center space-x-2">
            <button
              onClick={(e) => {
                e.preventDefault();
                parentNavigate("/dashboard");
              }}
              className="back-arrow -ml-3.5"
            >
              <ArrowLeft />
            </button>
            <div>
              <Breadcrumbs
                crumbs={[
                  { name: t("lang_dashboard"), path: `/dashboard` },
                  { name: t("lang_Portfolio_Categories"), path: "/portfolio/categories" },
                ]}
              />
              <h4 className="text-xl md:text-2xl font-semibold text-primary">
                {t("lang_Portfolio_Categories")}
              </h4>
            </div>
          </div>
          <div className="flex items-center">
            <ButtonComponent
              variant="primary"
              icon={<PlusCircle className="w-4 h-4" />}
              onClick={() => parentNavigate("/portfolio/categories/add")}
            >
              {t("Add Portfolio Category")}
            </ButtonComponent>
          </div>
        </div>
        <div className="flex justify-between w-full space-x-4">
          <div className="w-full space-y-3">
            <div className="flex w-full h-full overflow-hidden relative min-w-0">
              <div className="w-full">
                <div className="w-full transition-all duration-100 mb-[10px]">
                  <CommonTable
                    tableId="portfoliocategory"
                    columns={columns}
                    data={PortfolioCategory?.data?.records}
                    tableHeader={tableHeaderFilter}
                    onSort={SortData}
                    sortBy={sortBy}
                    orderBy={orderBy}
                    tableFooter={
                      (PortfolioCategory?.data?.totalRecords > 0 || PortfolioCategory?.data?.records?.length > 0) ? (
                        <PaginationComponent
                          count={PortfolioCategory?.data?.totalPages || 1}
                          page={Page}
                          onChange={(e, v) => setPage(v)}
                          totalRecords={PortfolioCategory?.data?.totalRecords || PortfolioCategory?.data?.records?.length || 0}
                          startRecord={startRec}
                          endRecord={endRec}
                          limit={limit}
                          recordsLabel="Records"
                        />
                      ) : null
                    }
                    loading={dataLoading}
                    emptyMessage={<NoRecords />}
                    rowClick={(row) => parentNavigate(`/portfolio/categories/${row._id}`)}
                    selectedRowsId={selectedRowsId}
                    setSelectedRowsId={setSelectedRowsId}
                    NEWAPI={handleTableApi}
                    tableDefaults={defaultCustomFields?.defaultPortfolioCategoryField}
                    tableWiseAllowedSortKeys={defaultCustomFields?.PORTFOLIO_CATEGORY_SORT_FIELD}
                    userState={userContext}
                  />
                </div>
              </div>
              <Popover
                open={filter}
                anchorEl={anchorEl}
                onClose={() => {
                  setFilter(false);
                  setAnchorEl(null);
                }}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "right",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
                PaperProps={{
                  style: { marginTop: "8px", minWidth: "300px", borderRadius: "8px" },
                }}
                className="rounded-[6px]"
              >
                <div className="p-[20px] filter secondary-bg-color">
                  <div className="flex justify-between items-center">
                    <p className="flex font-semibold uppercase text-sm m-0">
                      <Filter className="me-1 w-4 h-4" />
                      {t("lang_filter")}
                    </p>
                    <button
                      type="button"
                      onClick={(e) => resetFilter(e)}
                      className="text-white bg-gray-800 hover:bg-gray-900 focus:outline-none font-medium rounded-md text-sm px-5 py-2 me-1 mb-2 dark:bg-gray-800 dark:hover:bg-gray-700 dark:focus:ring-gray-700 dark:border-gray-700"
                    >
                      Reset Filter
                    </button>
                  </div>
                  <hr className="main-border-color my-3"></hr>
                  <div className="grid gap-y-5 table-text">
                    <div>
                      <span className="text-sm capitalize font-medium mb-1 inline-block">
                        {t("lang_Search")}
                      </span>
                      <SearchComponent
                        placeholder="Search Category"
                        value={search}
                        onChange={(e) => {
                          setSearch(e.target.value);
                          setPage(1);
                        }}
                        onClear={() => {
                          setSearch("");
                          setPage(1);
                        }}
                        iconPlacement="left"
                        isClearable={true}
                      />
                    </div>
                    <div>
                      <span className="text-sm table-text font-medium mb-1 inline-block">
                        {t("lang_set_items_limit")}
                      </span>
                      <PageLimitDropDownComponent
                        limit={limit}
                        setLimit={setLimit}
                        setPage={setPage}
                        totalRecords={PortfolioCategory?.data?.totalRecords || 0}
                      />
                    </div>
                  </div>
                </div>
              </Popover>
            </div>
          </div>
        </div>
      </div>

      {openBulkEditModal && (
        <Dialog
          open={openBulkEditModal}
          disableEscapeKeyDown={true}
          onClose={(event, reason) => {
            if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
              return;
            }
            setOpenBulkEditModal(false);
          }}
          fullScreen
          sx={{
            zIndex: 999999,
            "& .MuiDialog-paper": {
              zIndex: 999999,
              margin: 0,
              width: "100vw",
              height: "100vh",
              maxWidth: "100vw",
              maxHeight: "100vh",
              borderRadius: 0,
            },
          }}
        >
          <BulkEditPortfolioCategory
            isBulkEdit={true}
            bulkCategoryIds={selectedRowsId}
            onClose={() => {
              setOpenBulkEditModal(false);
              setSelectedRowsId([]);
            }}
            onSuccess={(updatedCount) => {
              setOpenBulkEditModal(false);
              setSelectedRowsId([]);
              setIsNotification(true);
              const count = typeof updatedCount === "number" ? updatedCount : 1;
              setNotificationMsg(
                `${count} categor${count > 1 ? "ies" : "y"} updated successfully!`
              );
              setSeverity("success");
              GetPortfolioCategoriesWithAllParent();
            }}
          />
        </Dialog>
      )}
    </>
  );
}

export default PortfolioCategory;
